import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employerAPI, paymentsAPI, reviewsAPI } from '../../services/api';
import { 
  ComplianceBanner, 
  ComplianceAcknowledgmentModal, 
  useComplianceState 
} from '../../components/common/ComplianceComponents';
import TrialExpiredModal from '../../components/common/TrialExpiredModal';
import QuickActionsWidget from '../../components/dashboard/QuickActionsWidget';
import CompanyTierCard from '../../components/common/CompanyTierCard';
import { 
  Search, Send, FileText, Bookmark, ChevronRight, Loader2, Crown, Star, Calendar, 
  Clock, AlertTriangle, ClipboardList, BarChart3
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState([]);
  const [scheduledReviews, setScheduledReviews] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  
  const {
    showComplianceModal,
    acknowledgeCompliance,
    dismissBanner
  } = useComplianceState(profile);

  useEffect(() => {
    checkOnboardingAndFetchData();
  }, []);

  const checkOnboardingAndFetchData = async () => {
    try {
      // First check if onboarding is complete
      const profileRes = await employerAPI.getProfile();
      if (!profileRes.data.onboarding_complete) {
        navigate('/employer/onboarding', { replace: true });
        return;
      }
      
      setProfile(profileRes.data);
      
      // If onboarding complete, fetch dashboard data
      const [statsRes, activityRes, scheduledRes, requestsRes] = await Promise.all([
        employerAPI.getDashboardStats(),
        employerAPI.getActivity(),
        reviewsAPI.getScheduledReviews().catch(() => ({ data: [] })),
        reviewsAPI.getReviewRequests().catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setScheduledReviews(scheduledRes.data || []);
      setReviewRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await paymentsAPI.createCheckout(planId, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Failed to start checkout:', error);
    }
  };

  const statCards = stats ? [
    { label: 'Active Searches', value: stats.active_searches, icon: Search, color: 'text-blue-400' },
    { label: 'Reach-Outs Sent', value: stats.reach_outs_sent, icon: Send, color: 'text-green-400' },
    { label: 'Records Submitted', value: stats.records_submitted, icon: FileText, color: 'text-amber-400' },
    { label: 'Candidates Saved', value: stats.candidates_saved, icon: Bookmark, color: 'text-purple-400' },
  ] : [];

  const quickActions = [
    { label: 'Search Talent', href: '/employer/search', icon: Search },
    { label: 'Submit a Record', href: '/employer/records/new', icon: FileText },
    { label: 'Reviews', href: '/employer/reviews', icon: ClipboardList },
    { label: 'Analytics', href: '/employer/analytics/performance', icon: BarChart3 },
    { label: 'Messages', href: '/employer/messages', icon: Send },
  ];

  // Show trial expired modal on first visit after trial expires
  useEffect(() => {
    if (stats && stats.subscription_tier === 'free' && !stats.trial_active) {
      // Check if we've already shown the modal this session
      const modalShown = sessionStorage.getItem('trial_expired_modal_shown');
      if (!modalShown) {
        setShowTrialExpiredModal(true);
        sessionStorage.setItem('trial_expired_modal_shown', 'true');
      }
    }
  }, [stats]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const showUpgradeBanner = stats?.subscription_tier === 'free' && !stats?.trial_active;

  const handleDismissBanner = async () => {
    await dismissBanner();
    setProfile(prev => ({ ...prev, compliance_banner_dismissed_at: new Date().toISOString() }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="employer-dashboard">
        {/* Compliance Acknowledgment Modal */}
        <ComplianceAcknowledgmentModal 
          open={showComplianceModal} 
          onAcknowledge={async () => {
            await acknowledgeCompliance();
            setProfile(prev => ({ ...prev, compliance_acknowledged_at: new Date().toISOString() }));
          }} 
        />
        
        {/* Trial Expired Modal */}
        <TrialExpiredModal
          open={showTrialExpiredModal}
          onClose={() => setShowTrialExpiredModal(false)}
        />
        
        {/* Compliance Banner */}
        <ComplianceBanner profile={profile} onDismiss={handleDismissBanner} />
        
        {/* Subscription Banner (only show when trial ended and not subscribed) */}
        {showUpgradeBanner && (
          <Card className="bg-gradient-to-r from-[#1B5AFF]/10 to-[#FFFFFF] border border-[#1B5AFF]/30 rounded-xl p-4 sm:p-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-[#1B5AFF]" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-[#0A0F1E]">Upgrade to access talent</h2>
                  <p className="text-[#5B6472] text-sm">Search verified candidates and send reach-outs</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  onClick={() => handleUpgrade('starter')}
                  variant="outline"
                  className="border-[#1B5AFF]/30 text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl text-sm"
                  data-testid="upgrade-starter-btn"
                >
                  Starter $299/mo
                </Button>
                <Button
                  onClick={() => handleUpgrade('growth')}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow text-sm"
                  data-testid="upgrade-growth-btn"
                >
                  Growth $799/mo
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Welcome Header */}
        <div className="animate-fade-in">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Employer Dashboard</h1>
          <p className="mt-2 text-[#5B6472] text-sm sm:text-base">
            {stats?.trial_active 
              ? "You're on a free trial with full Growth plan access."
              : stats?.subscription_tier === 'free' 
                ? 'Upgrade to start searching for verified talent.'
                : `You're on the ${stats?.subscription_tier} plan.`}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {statCards.map((stat, i) => (
            <Card 
              key={stat.label} 
              className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6 animate-fade-in"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="text-[#5B6472] text-xs sm:text-sm">{stat.label}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions Widget */}
        <QuickActionsWidget userType="employer" />

        {/* Company Tier Card */}
        <CompanyTierCard />

        {/* Reviews Section */}
        {(scheduledReviews.length > 0 || reviewRequests.length > 0) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#0A0F1E] flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#1B5AFF]" />
                Reviews
              </h2>
              <Link to="/employer/reviews">
                <Button variant="ghost" className="text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl text-sm">
                  View All
                </Button>
              </Link>
            </div>
            
            {/* Review Requests */}
            {reviewRequests.length > 0 && (
              <div className="mb-4">
                <p className="text-[#5B6472] text-sm mb-2">Pending Requests</p>
                <div className="space-y-2">
                  {reviewRequests.slice(0, 3).map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                          <Star className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] text-sm font-medium">{req.employee_name}</p>
                          <p className="text-[#5B6472] text-xs">Requested {req.preferred_cadence} review</p>
                        </div>
                      </div>
                      <Link to="/employer/reviews">
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-[#0A0F1E] rounded-lg h-8">
                          Review
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled Reviews */}
            {scheduledReviews.length > 0 && (
              <div>
                <p className="text-[#5B6472] text-sm mb-2">Upcoming Reviews</p>
                <div className="space-y-2">
                  {scheduledReviews.slice(0, 3).map((review) => (
                    <div 
                      key={review.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        review.status === 'overdue' 
                          ? 'bg-red-500/10 border border-red-500/20' 
                          : 'bg-[#F5F8FF]/50 border border-[#E8EDF5]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          review.status === 'overdue' ? 'bg-red-500/20' : 'bg-[#F5F8FF]'
                        }`}>
                          {review.status === 'overdue' ? (
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                          ) : (
                            <Calendar className="w-4 h-4 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${review.status === 'overdue' ? 'text-red-400' : 'text-[#0A0F1E]'}`}>
                            {review.employee_name}
                          </p>
                          <p className={`text-xs ${review.status === 'overdue' ? 'text-red-400/70' : 'text-[#5B6472]'}`}>
                            Due: {new Date(review.due_date).toLocaleDateString()}
                            {review.status === 'overdue' && ' (Overdue)'}
                          </p>
                        </div>
                      </div>
                      <Link to={`/employer/reviews/new?employee_id=${review.employee_id}&record_id=${review.record_id}&cadence=${review.review_type}`}>
                        <Button 
                          size="sm" 
                          className={`rounded-lg h-8 ${
                            review.status === 'overdue' 
                              ? 'bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF]' 
                              : 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                          }`}
                        >
                          Complete
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Recent Activity */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-xl font-semibold text-[#0A0F1E] mb-4">Recent Activity</h2>
            {activity.length > 0 ? (
              <div className="space-y-4">
                {activity.map((item, i) => (
                  <div key={item.id || i} className="flex items-start gap-3 pb-4 border-b border-[#E8EDF5] last:border-0 last:pb-0">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      item.type === 'record_created' ? 'bg-blue-400' :
                      item.type === 'record_acknowledged' ? 'bg-green-400' :
                      item.type === 'flag_raised' ? 'bg-red-400' :
                      item.type === 'review_requested' ? 'bg-amber-400' :
                      'bg-[#1B5AFF]'
                    }`} />
                    <div className="flex-1">
                      <p className="text-[#0A0F1E] text-sm">{item.message}</p>
                      <p className="text-[#5B6472] text-xs mt-1">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#5B6472] text-sm">No recent activity yet.</p>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-xl font-semibold text-[#0A0F1E] mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {quickActions.map((action) => (
                <Link
                  key={action.href}
                  to={action.href}
                  className="flex items-center justify-between p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5] hover:border-[#1B5AFF]/50 transition-colors group"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <action.icon className="h-5 w-5 text-[#1B5AFF]" />
                    <span className="text-[#0A0F1E] font-medium">{action.label}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#5B6472] group-hover:text-[#1B5AFF] transition-colors" />
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
